import { components } from "react-select";
import { POPULAR_CITIES } from "./constants";

/* ---------------- Custom option UI (City vs Point) ---------------- */
const LocationOption = (props) => {
  const { data } = props;
  const isPoint = data?.kind === "point";
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {data.label}
          </div>
          {isPoint && data.city ? (
            <div className="text-xs text-gray-500 truncate">{data.city}</div>
          ) : null}
        </div>
        {isPoint ? (
          <span className="text-[10px] px-2 py-1 rounded-full border bg-white text-gray-600 shrink-0">
            {data.pointType === "from" ? "BOARD AT" : "DROP AT"}
          </span>
        ) : (
          <span className="text-[10px] px-2 py-1 rounded-full bg-gray-50 text-gray-500 shrink-0">
            CITY
          </span>
        )}
      </div>
    </components.Option>
  );
};

const LocationSingleValue = (props) => {
  const { data } = props;
  const isPoint = data?.kind === "point";
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <span className="font-semibold">{data?.label}</span>
        {isPoint && data?.city ? (
          <span className="text-sm text-gray-500">• {data.city}</span>
        ) : null}
      </div>
    </components.SingleValue>
  );
};

/* ---------------- NEW: Match-first filtering + sorting (typeahead UX) ---------------- */
const rbFilterOption = (option, rawInput) => {
  const input = String(rawInput || "").trim().toLowerCase();
  if (!input) return true;

  const label = String(option?.label || "").toLowerCase();
  const city = String(option?.data?.city || "").toLowerCase(); // for points
  return label.includes(input) || city.includes(input);
};

const makeRbFilterSort = (rawInput) => {
  const input = String(rawInput || "").trim().toLowerCase();

  return (a, b) => {
    if (!input) return 0;

    const la = String(a?.label || "").toLowerCase();
    const lb = String(b?.label || "").toLowerCase();

    // 1) startsWith matches first
    const aStarts = la.startsWith(input) ? 0 : 1;
    const bStarts = lb.startsWith(input) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;

    // 2) then shorter label wins (often closer match)
    if (la.length !== lb.length) return la.length - lb.length;

    // 3) then alphabetical
    return la.localeCompare(lb);
  };
};

/* ---------------- NEW: Redbus-style MenuList (desktop dropdown) ---------------- */
const RedbusMenuList = (menuKey) => {
  return (props) => {
    const { selectProps } = props;
    const recents = selectProps?.recent?.[menuKey] || [];

    const flatten = (opts) => {
      const out = [];
      (opts || []).forEach((o) => {
        if (o?.options) out.push(...(o.options || []));
        else out.push(o);
      });
      return out;
    };

    const allOptions = flatten(selectProps?.options || []);

    const pickValue = (val) => {
      const found =
        allOptions.find((x) => x?.value === val) || {
          value: val,
          label: val,
          kind: "city",
        };

      // trigger react-select onChange so it closes + updates value
      try {
        selectProps?.onChange?.(found, {
          action: "select-option",
          option: found,
        });
      } catch {
        selectProps?.onChange?.(found);
      }
    };

    const isTyping = String(selectProps?.inputValue || "").trim().length > 0;

    return (
      <components.MenuList {...props}>
        {isTyping ? (
          <>
            {/* Suggested Cities / Points */}
            <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: "#F1F5F9" }}>
              <div className="text-sm font-bold text-gray-900">Suggested Cities / Points</div>
            </div>

            {/* Default options list (cities + points) */}
            <div className="px-0">{props.children}</div>

            {/* Clean separator */}
            <div className="h-2 bg-gray-50 border-y" style={{ borderColor: "#F1F5F9" }} />

            {/* Recent searches */}
            <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: "#F1F5F9" }}>
              <div className="text-sm font-bold text-gray-900">Recent searches</div>
            </div>

            <div className="px-2">
              {recents.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-400">No recent searches</div>
              ) : (
                <div className="mx-2 my-2 rounded-xl border border-gray-100 overflow-hidden">
                  {recents.map((r, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => pickValue(r)}
                      className="w-full px-3 py-3 text-left hover:bg-gray-50 active:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-900 truncate block">
                        {r}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Popular Cities */}
            <div className="px-4 pt-3 pb-2 border-t" style={{ borderColor: "#F1F5F9" }}>
              <div className="text-sm font-bold text-gray-900">Popular Cities</div>
            </div>

            <div className="px-2 pb-2">
              <div className="mx-2 my-2 rounded-xl border border-gray-100 overflow-hidden">
                {POPULAR_CITIES.slice(0, 6).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pickValue(c)}
                    className="w-full px-3 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-50"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Recent searches */}
            <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: "#F1F5F9" }}>
              <div className="text-sm font-bold text-gray-900">Recent searches</div>
            </div>

            <div className="px-2">
              {recents.length === 0 ? (
                <div className="px-3 py-3 text-sm text-gray-400">No recent searches</div>
              ) : (
                <div className="mx-2 my-2 rounded-xl border border-gray-100 overflow-hidden">
                  {recents.map((r, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => pickValue(r)}
                      className="w-full px-3 py-3 text-left hover:bg-gray-50 active:bg-gray-50"
                    >
                      <span className="text-sm font-semibold text-gray-900 truncate block">
                        {r}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Popular Cities */}
            <div className="px-4 pt-3 pb-2 border-t" style={{ borderColor: "#F1F5F9" }}>
              <div className="text-sm font-bold text-gray-900">Popular Cities</div>
            </div>

            <div className="px-2 pb-2">
              <div className="mx-2 my-2 rounded-xl border border-gray-100 overflow-hidden">
                {POPULAR_CITIES.slice(0, 6).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => pickValue(c)}
                    className="w-full px-3 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 active:bg-gray-50"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Clean separator */}
            <div className="h-2 bg-gray-50 border-y" style={{ borderColor: "#F1F5F9" }} />

            {/* Default options list (cities + points) */}
            <div className="px-0">{props.children}</div>
          </>
        )}
      </components.MenuList>
    );
  };
};

export {
  LocationOption,
  LocationSingleValue,
  rbFilterOption,
  makeRbFilterSort,
  RedbusMenuList,
};
